import React, { type FC, useEffect, useRef, useState } from "react";

import {
  ClientId,
  ConfigNamesFields,
  NamesOfFieldBankInfoEnum,
  NamesOfFieldEnum,
  TypeBankInfo
} from "~/typings/types";
import { ErrorField, Modal, MobileTooltip } from "~/components";
import { AutocompleteInput, ModalContent } from "./components";
import { useModal } from "~/hooks";

import styles from "./FormFieldAutocompleteBic.module.scss";
import { AnimatedContainer } from "~/containers";
import { BankConfirmCard } from "./components/BankConfirmCard";

const STORAGE_KEY_PREFIX = "confirmed_bank_";

export type ErrorRequest = {
  code: string;
  message: string;
} | null;

interface Props {
  name: string;
  clientId: ClientId;
  titleField?: string;
  validate?: any;
  required?: boolean;
  tooltip?: string;
  isColon?: boolean;
  styleContainerTitleField?: any;
  styleContainer?: any;
  placeholder?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  renderError?: any;
  propsSpy: any;
  configNameFieldsModalSuggestion: ConfigNamesFields;
  urlGetBankInfo: string;
  descriptionField?: string;
  onBankConfirmed?: (confirmed: boolean) => void;
}

export const FormFieldBic: FC<Props> = ({
  name,
  clientId,
  validate,
  styleContainer,
  renderError,
  configNameFieldsModalSuggestion,
  propsSpy,
  disabled,
  descriptionField,
  onBankConfirmed,
  ...propsAutocompleteInput
}) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${clientId}`;

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [errorGetSuggestions, setErrorGetSuggestions] = useState<ErrorRequest>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<TypeBankInfo>();
  const [confirmedBank, setConfirmedBank] = useState<TypeBankInfo | null>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? (JSON.parse(stored) as TypeBankInfo) : null;
    } catch {
      return null;
    }
  });
  const resetAutocompleteRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (confirmedBank) {
      propsSpy.form.change(NamesOfFieldEnum.BankName, confirmedBank[NamesOfFieldBankInfoEnum.BankName]);
      propsSpy.form.change(NamesOfFieldEnum.BankBic, confirmedBank[NamesOfFieldBankInfoEnum.BankBic]);
      propsSpy.form.change(NamesOfFieldEnum.CorrespondentAccount, confirmedBank[NamesOfFieldBankInfoEnum.BankCorrespondentAccount]);
      onBankConfirmed?.(true);
    }
  }, []);

  const customErrorField =
    (renderError?.has && renderError) || (errorGetSuggestions && { has: 1, message: errorGetSuggestions.message });

  const {
    isShow: isShowModal,
    open: openModal,
    toggle: toggleModal,
    close: closeModal
  } = useModal();

  const handleSelectedSuggestions = (suggestion: TypeBankInfo) => {
    propsSpy.form.change(NamesOfFieldEnum.BankName, "");
    propsSpy.form.change(NamesOfFieldEnum.CorrespondentAccount, "");
    setSelectedSuggestion(suggestion);
    openModal();
  };

  const handleSuccessModalSelectedSuggestions = () => {
    propsSpy.form.change(NamesOfFieldEnum.BankName, selectedSuggestion![NamesOfFieldBankInfoEnum.BankName]);
    propsSpy.form.change(NamesOfFieldEnum.BankBic, selectedSuggestion![NamesOfFieldBankInfoEnum.BankBic]);
    propsSpy.form.change(NamesOfFieldEnum.CorrespondentAccount, selectedSuggestion![NamesOfFieldBankInfoEnum.BankCorrespondentAccount]);
    setConfirmedBank(selectedSuggestion!);
    try {
      localStorage.setItem(storageKey, JSON.stringify(selectedSuggestion!));
    } catch {}
    onBankConfirmed?.(true);
    closeModal();
  };

  const handleRejectModalSelectedSuggestions = () => {
    closeModal();
  };

  const handleResetBank = () => {
    resetAutocompleteRef.current?.();
    setConfirmedBank(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {}
    setSelectedSuggestion(undefined);
    onBankConfirmed?.(false);
    propsSpy.form.change(NamesOfFieldEnum.BankBic, "");
    propsSpy.form.change(NamesOfFieldEnum.BankName, "");
    propsSpy.form.change(NamesOfFieldEnum.CorrespondentAccount, "");
  };

  return (
    <div className={styles.containerField} style={styleContainer}>
      <div className={styles.containerInput}>
        <AutocompleteInput
          {...propsAutocompleteInput}
          name={name}
          descriptionField={descriptionField}
          setErrorRequest={setErrorGetSuggestions}
          handleSelectedSuggestions={handleSelectedSuggestions}
          customError={!!customErrorField}
          setIsTooltipVisible={setIsTooltipVisible}
          isConfirmed={!!confirmedBank}
          registerReset={(fn: () => void) => { resetAutocompleteRef.current = fn; }}
        />
        {confirmedBank && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleResetBank}
            aria-label="Сбросить"
          >
            ×
          </button>
        )}
      </div>

      <AnimatedContainer isVisible={isTooltipVisible && !confirmedBank}>
        <MobileTooltip content={propsAutocompleteInput.tooltip} />
      </AnimatedContainer>

      {!confirmedBank && (
        <ErrorField name={name} renderError={customErrorField} />
      )}

      {confirmedBank && (
        <div style={{ marginTop: "32px",marginBottom: "32px" }}>
          <BankConfirmCard bank={confirmedBank} />
        </div>
      )}

      {selectedSuggestion && (
        <Modal isShow={isShowModal} toggle={toggleModal}>
          <ModalContent
            fields={selectedSuggestion}
            configNameFields={configNameFieldsModalSuggestion}
            handleClickSuccess={handleSuccessModalSelectedSuggestions}
            handleClickReject={handleRejectModalSelectedSuggestions}
          />
        </Modal>
      )}
    </div>
  );
};
